import {
  CONFIDENTIALITY_CODINGS_MAP,
  NORMAL_CONFIDENTIALITY_LEVEL_CODING,
  STANDALONE_NOTE_CATEGORY,
  TIMELINE_NOTE_COMMUNICATION_CATEGORY,
} from '@bariendo/lib/codings';
import { RICH_TEXT_CONTENT_PAYLOAD_ID, TEXT_CONTENT_PAYLOAD_ID } from '@bariendo/lib/constants';
import { EDITED_COMMUNICATION_PAYLOAD_EXTENSION } from '@bariendo/lib/extensions';
import { getConfidentialityLevel } from '@bariendo/lib/permissions';
import { MedplumClient, createReference, resolveId } from '@medplum/core';
import { Communication, Encounter, Organization, Patient, Practitioner, Reference } from '@medplum/fhirtypes';
import dayjs from 'dayjs';

export async function createStandaloneNote(
  medplum: MedplumClient,
  params: {
    currentUser: Practitioner;
    subject?: Reference<Patient>;
    recipient?: Reference<Organization>;
    content: string;
    richTextContent: string;
  }
) {
  const { subject, recipient, content, richTextContent, currentUser } = params;

  if (!currentUser) {
    throw new Error('currentUser is required');
  }

  if (!content) {
    throw new Error('content is required');
  }

  if (!subject && !recipient) {
    throw new Error('Either subject (Patient) or recipient (Organization) is required');
  }

  const unsecuredNote = await medplum.createResource<Communication>({
    resourceType: 'Communication',
    category: [{ coding: [STANDALONE_NOTE_CATEGORY] }],
    status: 'completed',
    ...(subject && { subject }),
    ...(recipient && { recipient: [recipient] }),
    sent: dayjs().toISOString(),
    sender: createReference(currentUser),
    payload: [
      {
        id: TEXT_CONTENT_PAYLOAD_ID,
        contentString: content,
      },
      {
        id: RICH_TEXT_CONTENT_PAYLOAD_ID,
        contentString: richTextContent,
      },
    ],
  });

  const securedNote = await medplum.updateResource({
    ...unsecuredNote,
    meta: {
      ...unsecuredNote.meta,
      security: [NORMAL_CONFIDENTIALITY_LEVEL_CODING],
    },
  });

  return securedNote;
}

export async function createTimelineNote(
  medplum: MedplumClient,
  params: {
    currentUser: Practitioner;
    communication: Reference<Communication | Encounter>;
    content: string;
    richTextContent?: object;
  }
): Promise<Communication> {
  const { communication, content, currentUser, richTextContent } = params;

  if (!currentUser) {
    throw new Error('currentUser is required');
  }

  if (!content) {
    throw new Error('content is required');
  }

  const referencedResource = await medplum.readReference(communication);
  const confidentialityLevel = getConfidentialityLevel(referencedResource);
  const confidentialityLevelCoding = confidentialityLevel
    ? CONFIDENTIALITY_CODINGS_MAP[confidentialityLevel]
    : NORMAL_CONFIDENTIALITY_LEVEL_CODING;

  const unsecuredTimelineNote = await medplum.createResource<Communication>({
    resourceType: 'Communication',
    category: [{ coding: [TIMELINE_NOTE_COMMUNICATION_CATEGORY] }],
    status: 'completed',
    sent: dayjs().toISOString(),
    subject: referencedResource?.subject,
    ...(communication && { basedOn: [communication] }),
    sender: createReference(currentUser),
    payload: [
      {
        id: TEXT_CONTENT_PAYLOAD_ID,
        contentString: content,
      },
      ...(richTextContent
        ? [
            {
              id: RICH_TEXT_CONTENT_PAYLOAD_ID,
              contentString: JSON.stringify(richTextContent),
            },
          ]
        : []),
    ],
  });

  const timelineNote = await medplum.updateResource({
    ...unsecuredTimelineNote,
    meta: {
      ...unsecuredTimelineNote.meta,
      security: [confidentialityLevelCoding],
    },
  });

  return timelineNote;
}

export async function updateTimelineNote(
  medplum: MedplumClient,
  params: {
    noteRef: Reference<Communication>;
    content: string;
    richTextContent?: object;
  }
): Promise<Communication> {
  const { noteRef, content, richTextContent } = params;

  if (!content) {
    throw new Error('content is required');
  }

  const note = await medplum.readReference(noteRef);

  const payload = (note.payload ?? []).filter(
    (p) => p.id !== TEXT_CONTENT_PAYLOAD_ID && p.id !== RICH_TEXT_CONTENT_PAYLOAD_ID
  );

  const updatedNote = await medplum.updateResource({
    ...note,
    payload: [
      ...payload,
      {
        id: TEXT_CONTENT_PAYLOAD_ID,
        contentString: content,
        extension: [{ url: EDITED_COMMUNICATION_PAYLOAD_EXTENSION, valueDateTime: new Date().toISOString() }],
      },
      ...(richTextContent
        ? [
            {
              id: RICH_TEXT_CONTENT_PAYLOAD_ID,
              contentString: JSON.stringify(richTextContent),
              extension: [{ url: EDITED_COMMUNICATION_PAYLOAD_EXTENSION, valueDateTime: new Date().toISOString() }],
            },
          ]
        : []),
    ],
  });

  return updatedNote as Communication;
}

export async function deleteTimelineNote(
  medplum: MedplumClient,
  params: {
    noteRef: Reference<Communication>;
  }
): Promise<void> {
  const { noteRef } = params;

  await medplum.deleteResource('Communication', resolveId(noteRef)!);
}
